import React from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { SUBDOMAIN } from '../../assets/meta/meta';
import { useAuthContext } from '../../Components/AuthContext';
import { Button } from '@/Components/ui';
import { siteService } from '@/api/services/site.service';
import { authService } from '@/api/services/auth.service';
import { dialogService } from "@/utils/dialogService";
import { isLoginPersistenceEnabled } from '../../api/auth';
import NewAdminNotice from '../../Components/Common/NewAdminNotice';
const Login = (props) => {
    const navigate = useNavigate();
    const [isWrong, setIsWrong] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [loginLogo, setLoginLogo] = React.useState<string | null>(null);
    const [siteName, setSiteName] = React.useState("AmazingTicket");
    const { login, userInfo } = useAuthContext();
    // 결정 313: 카카오(어메이징 관리자) 로그인 버튼 + ADMIN_LEGACY_LOGIN=off면 기존 폼 숨김
    const [loginConfig, setLoginConfig] = React.useState<{ legacy_login: boolean; sso_ready: boolean; sso_login_url: string } | null>(null);
    React.useEffect(() => {
        authService.getAdminLoginConfig().then((r) => setLoginConfig(r.data)).catch(() => setLoginConfig(null));
    }, []);
    const legacyLogin = loginConfig ? loginConfig.legacy_login : true;
    const keepLoginInitialValue = React.useMemo(() => isLoginPersistenceEnabled(), []);
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            phoneNum: '',
            password: '',
            keepLogin: keepLoginInitialValue,
        },
        validationSchema: Yup.object({
            phoneNum: Yup.string()
                .matches(/^\d+$/, "전화번호를 입력해주세요.('-' 제외)")
                .required("전화번호를 입력해주세요.('-' 제외)"),
            password: Yup.string().required("올바른 비밀번호를 입력해주세요."),
        }),
        onSubmit: (values) => {
            const normalizedPhone = values.phoneNum.replace(/\D/g, '');
            login({
                username: normalizedPhone.startsWith('0') ? normalizedPhone : `0${normalizedPhone}`,
                password: values.password,
                keepLogin: values.keepLogin,
            }).catch(async (e) => {
                setIsWrong(true);
                await dialogService.handleAlert(`로그인 실패: ${e.message}`, { force: "error" });
            });
        }
    });
    // React.useEffect(() => {
    //     const { naver } = window
    //     const naverLogin = new naver.LoginWithNaverId(
    //       {
    //         clientId: CLIENT_ID,
    //         callbackUrl: CALLBACK_URL,
    //         isPopup: false, /* 팝업을 통한 연동처리 여부, true 면 팝업 */
    //         loginButton: { color: "green", type: 3, height: 47, width: "100%" } /* 로그인 버튼의 타입을 지정 */
    //       }
    //     );
    //     naverLogin.init();
    // }, []);
    React.useEffect(() => {
        let isMounted = true;
        siteService
            .getSitesApp()
            .then((response) => {
                if (!isMounted)
                    return;
                const sites = response.data?.result?.sites || [];
                const normalizedSubdomain = SUBDOMAIN?.replace(/crm$/, "");
                const matchedSite = sites.find((site) => site.subdomain === SUBDOMAIN || site.subdomain === normalizedSubdomain);
                setLoginLogo(matchedSite?.logo || null);
                setSiteName(matchedSite?.name || "AmazingTicket");
            })
            .catch(() => {
                if (!isMounted)
                    return;
                setLoginLogo(null);
                setSiteName("AmazingTicket");
            });
        return () => {
            isMounted = false;
        };
    }, []);
    React.useEffect(() => {
        const validatePermission = async () => {
            // 이미 로그인한 상태에서 잘못된 접근을 막는 코드
            if (!userInfo)
                return;
            console.log("permission check user:", userInfo.site_permission, userInfo.capabilities);
            const capabilities = new Set(Array.isArray(userInfo.capabilities) ? userInfo.capabilities : []);
            const hasCapability = (capability) => Boolean(userInfo.is_global_admin || capabilities.has(capability));
            if (hasCapability("crm_dashboard")) {
                navigate(`/dashboard-crm`);
            }
            else if (hasCapability("marketer")) {
                navigate(`/marketer-info`);
            }
            else if (userInfo.site_permission && hasCapability("partner_dashboard")) {
                navigate(`/daily-reservation-list`);
            }
            else if (userInfo.site_permission && hasCapability("delivery_dashboard")) {
                navigate(`/shopping-admin`);
            }
            else {
                await dialogService.handleAlert("관리자 및 허용된 스탭 만 로그인할 수 있습니다!");
            }
        };
        void validatePermission();
    }, [userInfo, navigate]);
    return (<React.Fragment>
        <div className="auth-page-content">
            <div className="container-fluid">
                <div className="flex flex-wrap -mx-3">
                    <div className="w-full px-3">
                        <div className="text-center mt-sm-5 mb-4 text-white-50">
                            <div>
                                {loginLogo && (<Link to="/" className="d-inline-block auth-logo">
                                    <img
                                        src={loginLogo}
                                        alt={`${siteName} logo`}
                                        className='object-contain h-58 w-auto'
                                    />
                                </Link>)}
                            </div>
                            {/* <p className="mt-3 fs-15 fw-medium">Premium Admin & Dashboard Template</p> */}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap -mx-3 justify-content-center">
                    <div className="w-full md:w-8/12 lg:w-6/12 xl:w-5/12 px-3">
                        <div className="card">
                            <div className="card-body p-4">
                                <div className="text-center mt-2">
                                    <h5 className="text-primary">{siteName}</h5>
                                    <p className="text-muted">Sign in to continue.</p>
                                </div>
                                {/* {error && error ? (<Alert color="danger"> {error} </Alert>) : null} */}
                                <div className="p-2 mt-4">
                                    {loginConfig?.sso_ready ? (
                                        <div className="mb-4">
                                            {/* B 지시 40: 기존 로그인은 그대로, 카카오 버튼 위 안내 한 줄 */}
                                            <p className="text-muted small mb-2">새 관리자 시스템으로 이동 중입니다. 카카오 로그인은 점장이 crm에서 초대한 직원만 쓸 수 있어요.</p>
                                            <a href={loginConfig.sso_login_url} className="btn w-100" style={{ backgroundColor: "#fee500", color: "#191919", fontWeight: 600 }}>카카오로 로그인(어메이징 관리자)</a>
                                        </div>
                                    ) : null}
                                    {legacyLogin ? (
                                    <form onSubmit={(e) => {
                                        e.preventDefault();
                                        validation.handleSubmit();
                                        return false;
                                    }} action="#">

                                        <div className="mb-3">
                                            <label htmlFor="phoneNum" className="form-label">휴대폰 번호</label>
                                            <input name="phoneNum" className="form-control" placeholder="전화번호를 입력해주세요.('-' 제외)" type="tel" inputMode="numeric" value={validation.values.phoneNum} onChange={(event) => {
                                                setIsWrong(false);
                                                validation.handleChange(event);
                                            }} onBlur={validation.handleBlur} autoComplete="username" />
                                            {(validation.touched.phoneNum && validation.errors.phoneNum) ? (<div className="invalid-feedback d-block">{(validation.errors.phoneNum)}</div>) : null}
                                            {(isWrong) ? <span className="text-danger">*올바른 휴대폰 번호를 입력해주세요.</span> : null}
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="password" className="form-label">비밀번호</label>
                                            <div className="relative">
                                                <input name="password" className="form-control pr-12" placeholder="비밀번호를 입력해주세요." type={showPassword ? "text" : "password"} value={validation.values.password} onChange={(event) => {
                                                    setIsWrong(false);
                                                    validation.handleChange(event);
                                                }} onBlur={validation.handleBlur} autoComplete="current-password" />
                                                <button type="button" className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500" onClick={() => setShowPassword((prev) => !prev)} aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}>
                                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                            {(validation.touched.password && validation.errors.password) ? (<div className="invalid-feedback d-block">{(validation.errors.password)}</div>) : null}
                                            {(isWrong) ? <span className="text-danger">*올바른 비밀번호를 입력해주세요.</span> : null}
                                        </div>
                                        <div className="mb-4">
                                            <div className="form-check">
                                                <input id="keepLogin" name="keepLogin" className="form-check-input" type="checkbox" checked={validation.values.keepLogin} onChange={validation.handleChange} />
                                                <label htmlFor="keepLogin" className="form-check-label">로그인 유지</label>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <Button type="submit" color="success" className="w-100">Sign In</Button>
                                        </div>
                                        <div className="mt-4 text-center">
                                            {/* <div className="signin-other-title">
                <h5 className="fs-13 mb-4 title">Sign In with</h5>
            </div>
            <div>
                <button style={{border: 0, backgroundColor: "#fee500", width: "100%", borderRadius: 12}} onClick={() => { window.location.replace(KAKAO_AUTH_URL) }}>
                    <img className='img-fluid' src={KAKAO_BTN} alt=""/>
                </button>
            </div> */}
                                            {/* <br/> */}
                                            {/* <div>
                <div style={{border: 0, backgroundColor: "#1ec800", borderRadius: 12}} className="grid-naver" id='naverIdLogin'></div>
            </div> */}
                                        </div>
                                    </form>
                                    ) : <p className="text-muted text-center">비밀번호 로그인은 꺼졌어요. 위 카카오 로그인을 써 주세요.</p>}
                                </div>
                            </div>
                        </div>
                        <NewAdminNotice />

                    </div>
                </div>
            </div>
        </div>
    </React.Fragment>);
};
export default Login;
